/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  sanitizeAccount,
  syncableAccountUpdateProps,
} from "../../../graphql/account/accountCRUD";

import { Tooltip } from "@material-ui/core";

import { useNotify, Confirm, SaveButton } from "react-admin";
import { useForm, useFormState } from "react-final-form";

import { getCustomFieldId } from "../../../components/common/CustomFields";
import {
  ImportExportIcon,
  SaveIcon,
  UploadIcon,
} from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import {
  AccountStatus,
  ExternalStatus,
  FilePaths,
  ResourceType,
  VALIDATION_MSG,
} from "../../../utils/constants";
import { saveRemoveFile, saveRemoveFiles } from "../../../utils/fileUtils";
import {
  triggerEvent,
  sanitizeZapierData,
  checkDirtySync,
} from "../../../utils/zapierUtils";
import { deepRedirect, autogenHistoryNotes } from "../../../utils/utils";
import {
  lotPrefixTakenAtSave,
  normalizeWebsiteUrl,
  setAccountSyncFailed,
  validateEmailUnicity,
  waitForFormValidation,
} from "./AccountUtils";

export const DialogSaveButton = ({
  label,
  basePath,
  backPath,
  record,
  isDeleting,
  isSaving,
  setIsSaving,
  isSyncEnabled,
  isLocked,
  ...props
}) => {
  const form = useForm();
  const { pristine, dirtyFields, submitting } = useFormState({
    subscription: { pristine: true, dirtyFields: true, submitting: true },
  });
  const notify = useNotify();
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [allMessages, setAllMessages] = useState([]);
  const [confirmMessageIdx, setConfirmMessageIdx] = useState();
  const skipInvoice = form.getFieldState("skipInvoice");
  // Synchronous guard against double-submission. RA3's setTransform installs a
  // one-shot wrapper that self-clears after one call; a second concurrent save
  // would otherwise submit raw, un-sanitized data.
  const isSavingRef = useRef(false);

  // skipInvoice comes from getFieldState (fresh object each render), so this
  // is a plain per-render computation, not a memo.
  const dirtyInvoicing = skipInvoice?.dirty && skipInvoice?.value === true;
  const isSyncDirty =
    isSyncEnabled &&
    (checkDirtySync(dirtyFields, syncableAccountUpdateProps) || dirtyInvoicing);

  const unlock = () => {
    isSavingRef.current = false;
    setIsSaving(false);
  };

  // The form stays locked (Save disabled, backdrop up) from the click through
  // validation, the confirmation dialog(s) and the submit. The Confirm dialog
  // renders above the backdrop, so it stays usable while locked.
  const handleSave = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);

    // Async field validators (lot prefix unicity) may still be running.
    await waitForFormValidation(form);
    if (form.getState().invalid || (await lotPrefixTakenAtSave(form))) {
      // Blocked by validation, so nothing is submitted; final-form marks every
      // field touched and react-admin paints the errors and the tab.
      form.submit();
      notify(VALIDATION_MSG, "warning");
      unlock();
      return;
    }

    const formState = form.getState();
    const formData = formState.values;
    const newMessages = [];

    if (formState.dirtyFields.email) {
      const emailWarning = await validateEmailUnicity(formData.email, formData);
      if (emailWarning) newMessages.push(emailWarning.message);
    }

    if (newMessages.length > 0) {
      setAllMessages(newMessages);
      setConfirmMessageIdx(1);
      setOpen(true);
    } else {
      setConfirmMessageIdx(null);
      props.handleSubmitWithRedirect();
    }
  };

  // Steps through allMessages one dialog at a time, then submits.
  const handleValidateSave = () => {
    setOpen(false);

    if (allMessages[confirmMessageIdx]) {
      setConfirmMessageIdx(confirmMessageIdx + 1);
      setOpen(true);
    } else {
      props.handleSubmitWithRedirect();
    }
  };

  const handleDialogClose = () => {
    unlock();
    setOpen(false);
  };

  const transform = async (data) => {
    data = { ...data };

    data.status =
      data.status === AccountStatus.NEW ? AccountStatus.ACTIVE : data.status;

    data.externalId = data.externalId || " ";

    const includeArrayList = [
      "inboundCustomFields.name",
      "inventoryCustomFields.name",
      "outboundCustomFields.name",
    ];
    autogenHistoryNotes(
      form,
      data.id,
      ResourceType.ACCOUNT,
      [],
      includeArrayList,
    );

    data.businessName = data.businessName.trim();
    data.displayName = data.displayName?.trim() || "";
    data.email = data.email.trim().toLowerCase();
    if (data.address?.state)
      data.address.state = data.address.state.toUpperCase();
    if (data.address?.zipCode)
      data.address.zipCode = data.address.zipCode.toUpperCase();
    data.addressState = data.address?.state ? data.address.state : " ";
    delete data.address?.name;

    if (data.billingEmail)
      data.billingEmail = data.billingEmail.trim().toLowerCase() || null;

    // Billing name is never edited on its own, it mirrors the account name,
    // so a rename has to carry over to the stored billing address.
    if (!data.skipInvoice)
      data.billingAddress = { ...data.billingAddress, name: data.businessName };
    if (data.billingAddress?.state)
      data.billingAddress.state = data.billingAddress.state.toUpperCase();
    if (data.billingAddress?.zipCode)
      data.billingAddress.zipCode = data.billingAddress.zipCode.toUpperCase();

    if (data.inboundCustomFields?.length > 0) {
      data.inboundCustomFields.forEach(
        (itm) => (itm.id = itm.id ? itm.id : getCustomFieldId(itm.name)),
      );
    }
    if (data.outboundCustomFields?.length > 0) {
      data.outboundCustomFields.forEach(
        (itm) => (itm.id = itm.id ? itm.id : getCustomFieldId(itm.name)),
      );
    }
    if (data.inventoryCustomFields?.length > 0) {
      data.inventoryCustomFields.forEach(
        (itm) => (itm.id = itm.id ? itm.id : getCustomFieldId(itm.name)),
      );
    }

    data.inboundPhotos = data.inboundPhotos || 0;
    data.outboundPhotos = data.outboundPhotos || 0;
    data.inventoryPhotos = data.inventoryPhotos || 0;

    try {
      await saveRemoveFiles(
        record,
        data,
        "attachments",
        `${FilePaths.ACCOUNT}/${data.id}`,
      );
      await saveRemoveFile(
        record,
        data,
        "bolTemplate",
        `${FilePaths.ACCOUNT}/${data.id}`,
      );
      await saveRemoveFile(
        record,
        data,
        "receiptTemplate",
        `${FilePaths.ACCOUNT}/${data.id}`,
      );
    } catch (err) {
      logError("accounts:DialogSaveButton:saveRemoveFile", err, notify);
      unlock();
      return Promise.reject();
    }

    if (isSyncEnabled && isSyncDirty) {
      data.externalStatus = ExternalStatus.SYNC_PENDING;
    }

    // edit form side-effect cleanup
    delete data.servicesFile;
    delete data.chargeRulesFile;
    delete data.productFile;

    const sanitized = sanitizeAccount(data);

    return sanitized;
  };

  const onSuccess = async ({ data }) => {
    try {
      if (isSyncEnabled && isSyncDirty) {
        const sanitizedData = sanitizeZapierData(
          syncableAccountUpdateProps,
          data,
        );
        const normalizedData = {
          ...sanitizedData,
          website: normalizeWebsiteUrl(sanitizedData.website),
        };
        triggerEvent(normalizedData, "accountUpdated").catch((error) => {
          logError("accounts:DialogSaveButton:triggerEvent", error);
          setAccountSyncFailed(data);
        });
      }
      notify(`Account updated successfully.`, "success");
      deepRedirect(history, backPath ? backPath : basePath);
    } catch (error) {
      setAccountSyncFailed(data);
      logError("accounts:DialogSaveButton:onSuccess", error, notify);
    } finally {
      unlock();
    }
  };

  const onFailure = (error) => {
    unlock();
    logError(
      "accounts:DialogSaveButton:onFailure:Account Update",
      error,
      notify,
    );
  };

  if (isLocked)
    return (
      <Tooltip title="Account is syncing.">
        <span>
          <SaveButton label={label} icon={<ImportExportIcon />} disabled />
        </span>
      </Tooltip>
    );
  return (
    <>
      <SaveButton
        label={label}
        icon={isSyncDirty ? <UploadIcon /> : <SaveIcon />}
        submitOnEnter={false}
        undoable={false}
        disabled={pristine || isSaving || isDeleting}
        saving={isSaving || submitting}
        handleSubmitWithRedirect={handleSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <Confirm
        isOpen={open}
        title="Save Confirmation"
        content={allMessages[confirmMessageIdx - 1]}
        onConfirm={handleValidateSave}
        onClose={handleDialogClose}
      />
    </>
  );
};
